/**
 * Everything the Engagement tab shows, derived from the stored tiles.
 *
 * Kept out of the components so the page is a re-skin away from any other
 * surface: the arithmetic and formatting live here, the markup renders what
 * this returns. A suppressed tile becomes `null` all the way through, never
 * a zero — "not enough data yet" and "none" are different answers.
 */
import type {
  EngagementTiles,
  Tile,
} from "apps/company/api/rest/company-dashboard-tiles";

export type Stat = { value: string | null; share: string | null };

export type FunnelStep = Stat & {
  // 0–1 against the first stage, for the bar's length.
  width: number;
};

export type TopicRow = {
  topic: string;
  share: string;
  // 0–1 of the topic-tagged completions, for the donut segment.
  fraction: number;
};

export type PopularRow = {
  title: string;
  minutes: string;
  // 0–1 against the most engaged item, for the bar's length.
  width: number;
};

export type EngagementView = {
  employeesInScope: Stat;
  activated: Stat;
  everEngaged: Stat;
  engagedOfWorkforce: string | null;
  minutes: { value: string | null; average: string | null };
  funnel: {
    activated: FunnelStep;
    everEngaged: FunnelStep;
    repeatEngaged: FunnelStep;
  };
  topics: {
    rows: TopicRow[];
    counted: string;
    total: string;
    complete: boolean;
  } | null;
  popular: { rows: PopularRow[]; top: PopularRow | null } | null;
};

export function engagementView(
  tiles: EngagementTiles,
  locale: string
): EngagementView {
  const count = (n: number) => new Intl.NumberFormat(locale).format(n);
  const percent = (part: number, whole: number) =>
    new Intl.NumberFormat(locale, {
      style: "percent",
      maximumFractionDigits: 0,
    }).format(part / whole);

  const value = (tile: Tile<number>) => (tile.suppressed ? null : tile.value);
  // A share of a withheld figure, or of one, would reveal it — both sides
  // must be shown for the share to be.
  const share = (part: number | null, whole: number | null) =>
    part === null || whole === null || whole === 0
      ? null
      : percent(part, whole);
  const stat = (part: number | null, whole: number | null): Stat => ({
    value: part === null ? null : count(part),
    share: share(part, whole),
  });

  const inScope = value(tiles.employees_in_scope);
  const activated = value(tiles.activated);
  const everEngaged = value(tiles.ever_engaged);
  const repeatEngaged = value(tiles.repeat_engaged);

  // Each funnel stage is a share of the one before it; the bars are all
  // measured against the first, so the funnel narrows as it reads down.
  const step = (part: number | null, previous: number | null): FunnelStep => ({
    ...stat(part, previous),
    width: part === null || !activated ? 0 : Math.min(part / activated, 1),
  });

  return {
    employeesInScope: stat(inScope, null),
    activated: stat(activated, inScope),
    everEngaged: stat(everEngaged, activated),
    engagedOfWorkforce: share(everEngaged, inScope),
    minutes: minutesView(tiles.content_duration_minutes, locale),
    funnel: {
      activated: step(activated, inScope),
      everEngaged: step(everEngaged, activated),
      repeatEngaged: step(repeatEngaged, everEngaged),
    },
    topics: topicsView(tiles.topic_split, count, percent),
    popular: popularView(tiles.popular_content, count),
  };
}

function minutesView(
  tile: EngagementTiles["content_duration_minutes"],
  locale: string
): EngagementView["minutes"] {
  if (tile.suppressed) return { value: null, average: null };
  const { minutes, engagements } = tile.value;
  return {
    value: new Intl.NumberFormat(locale).format(minutes),
    average:
      engagements === 0
        ? null
        : new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
            minutes / engagements
          ),
  };
}

function topicsView(
  tile: EngagementTiles["topic_split"],
  count: (n: number) => string,
  percent: (part: number, whole: number) => string
): EngagementView["topics"] {
  if (tile.suppressed) return null;
  const { topics, untagged, without_metadata } = tile.value;
  const counted = topics.reduce((sum, t) => sum + t.completions, 0);
  const total = counted + untagged + without_metadata;
  // Nothing tagged means nothing to split — shown as "none", not as a
  // chart of divisions by zero.
  if (counted === 0) {
    return {
      rows: [],
      counted: count(0),
      total: count(total),
      complete: total === 0,
    };
  }
  return {
    rows: topics.map((t) => ({
      topic: t.topic,
      // Share of the topic-tagged completions: nearly half of all
      // completions are articles, which carry no topic, and folding them
      // into every percentage would shrink each segment without telling
      // anyone why. The coverage line says what the split is drawn from.
      share: percent(t.completions, counted),
      fraction: t.completions / counted,
    })),
    counted: count(counted),
    total: count(total),
    complete: counted === total,
  };
}

function popularView(
  tile: EngagementTiles["popular_content"],
  count: (n: number) => string
): EngagementView["popular"] {
  if (tile.suppressed) return null;
  // Ordered here rather than trusted from the endpoint, so the leader named
  // in the summary is the item with the most minutes whatever order arrives.
  const items = [...tile.value].sort(
    (a, b) => b.minutes - a.minutes || a.title.localeCompare(b.title)
  );
  const largest = Math.max(0, ...items.map((item) => item.minutes));
  const rows = items.map((item) => ({
    title: item.title,
    minutes: count(item.minutes),
    width: largest === 0 ? 0 : item.minutes / largest,
  }));
  return { rows, top: rows[0] ?? null };
}

/** "3 hours ago", "yesterday" — in the viewer's language. */
export function updatedAgo(
  computedAt: Date,
  now: Date,
  locale: string
): string {
  const seconds = Math.round((computedAt.getTime() - now.getTime()) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) {
      return format.format(Math.round(seconds / size), unit);
    }
  }
  return format.format(0, "minute");
}
